import React, { useEffect, useRef, useState } from 'react';
import {
  mdiBankOutline,
  mdiCardBulletedOutline,
  mdiCashClock,
  mdiCashFast,
  mdiClose,
  mdiHelpCircleOutline,
  mdiMenu,
  mdiPiggyBankOutline,
  mdiViewGrid
} from '@mdi/js';
import { HeaderLogo, IconButton, Link, Padding } from '@upgrade/react-components';
import env from '@upgrade/ui-utils/env';
import { debounce } from 'lodash';

import useClickOutside from '../../../utils/useClickOutside';
import Fluid from '../../Fluid';
import HeaderContainer, { NavigationContainer, MobileHeader, StyledMobileSignInBtn } from './styles';
import MenuItem from './menu-item';
import useWindowSize from '../../../utils/useWindowSize';
import PartnerLogo from '../../PartnerLogo';
import { HEADER_ID } from '../../../constants/ids-constants';
import GatsbyLink from '../Link';
import {
  ATM_NETWORK_PATH,
  CH_PATH,
  CREDIT_HEALTH_INSIGHTS_PATH,
  FLEX_PAY,
  FLEX_PAY_FIND_PARTNERS,
  FLEX_PAY_PARTNERS,
  PCL_CASH_CARD_PATH,
  PCL_LIFE_CARD_PATH,
  PCL_ONE_CARD_PATH,
  PCL_SELECT_CARD_PATH,
  PL_DC_PATH,
  PL_HI_PATH,
  PL_MP_PATH,
  PL_PATH,
  PL_RCC_PATH,
  PL_UE_PATH,
  PREMIER_SAVINGS_PATH,
  REWARDS_CHECKING_PLUS_PATH,
  UPGRADE_CARD_PATH
} from '../../../constants/path-constants';
import SubMenuItem from './SubMenuItem';

import MenuWrapper from './menu-wrapper';

const menuIconProps = {
  label: 'open navigation',
  size: '1.5rem',
  className: 'hidden-md'
};

const NavContainer = ({ children, navRef }) => {
  return (
    <nav aria-label="main navigation">
      <ul ref={navRef}>{children}</ul>
    </nav>
  );
};

const atmNetworkPageEnabled =
  env.GATSBY_ATM_NETWORK_ENABLED === 'true' || process.env.GATSBY_ATM_NETWORK_ENABLED === 'true';

const HeaderMenu = ({
  customLogo,
  customLogoHref,
  customNavItems,
  customNavigation,
  customMobileNavigation,
  signInHref,
  signInText,
  showNavigation = true,
  showPartnerLogo,
  partnerLogo,
  showNavItems = true,
  isNotFoundPage
}) => {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [visibleSubMenuId, setVisibleSubMenuId] = useState('');
  const [addHeaderShadow, setAddHeaderShadow] = useState(false);
  const navContainerRef = useRef();
  const openMenuButtonRef = useRef();
  const { width: windowWidth } = useWindowSize();

  useClickOutside(navContainerRef, () => setVisibleSubMenuId(''));

  useEffect(() => {
    if (showMobileMenu && windowWidth < 1280) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showMobileMenu, windowWidth]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 150) {
        setAddHeaderShadow(true);
        setVisibleSubMenuId('');
      } else {
        setAddHeaderShadow(false);
      }
    };
    const debouncedHandleScroll = debounce(handleScroll, 10);
    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    // Move the focus to the first item in the navigation when the mobile menu is opened
    if (showMobileMenu) {
      navContainerRef.current.querySelector('a')?.focus();
    }
  }, [showMobileMenu]);

  const handleMenuClick = id => event => {
    event.preventDefault();

    if (visibleSubMenuId === id) {
      setVisibleSubMenuId('');
      return;
    }

    setVisibleSubMenuId(id);
  };

  const handleCloseMenu = () => {
    setShowMobileMenu(false);
    openMenuButtonRef.current?.focus();
  };

  const headerLogoHref = customLogoHref || '/';
  const isFlexPayPagesEnabled =
    env.GATSBY_FLEX_PAY_PAGES_ENABLED === 'true' || process.env.GATSBY_FLEX_PAY_PAGES_ENABLED === 'true';

  return (
    <HeaderContainer id={HEADER_ID} className="desktop-header" addHeaderShadow={addHeaderShadow}>
      <Fluid>
        {showNavigation && showNavItems !== false && !isNotFoundPage && (
          <IconButton
            {...menuIconProps}
            ref={openMenuButtonRef}
            icon={mdiMenu}
            color="actionablePrimary"
            onClick={() => setShowMobileMenu(true)}
            onKeyDown={handleMenuClick('')}
          />
        )}

        <HeaderLogo {...{ Link: GatsbyLink, linkProps: { to: headerLogoHref }, logoMobileLarge: true, customLogo }} />

        {showNavigation && (
          <MenuWrapper visible={showMobileMenu}>
            <MobileHeader>
              <IconButton
                {...{ ...menuIconProps, label: 'close navigation' }}
                icon={mdiClose}
                color="content"
                onClick={handleCloseMenu}
                onKeyDown={handleMenuClick('')}
              />
              <div>
                <HeaderLogo
                  {...{ Link: GatsbyLink, linkProps: { to: headerLogoHref }, logoMobileLarge: true, customLogo }}
                />
              </div>
            </MobileHeader>

            {showNavItems !== false && (
              <NavContainer navRef={navContainerRef} showMobileMenu={showMobileMenu}>
                {customNavItems ? (
                  customNavItems({ showMobileMenu, setShowMobileMenu })
                ) : (
                  <>
                    <MenuItem
                      icon={mdiCashFast}
                      label="Personal Loans"
                      visibleSubMenuId={visibleSubMenuId}
                      id="pl"
                      onClick={handleMenuClick('pl')}
                      to={isNotFoundPage ? PL_PATH : ''}
                    >
                      <SubMenuItem
                        label="All Personal Loans"
                        bold
                        to={PL_PATH}
                        img={{ src: '/img/new-home-page/new-pl.png' }}
                      />
                      <SubMenuItem
                        label="Refinance Credit Cards"
                        img={{ src: '/img/refinance-credit-icon.svg' }}
                        to={PL_RCC_PATH}
                      />
                      <SubMenuItem
                        label="Debt Consolidation"
                        img={{ src: '/img/pl-categories-home/DebtConsolidation.png' }}
                        to={PL_DC_PATH}
                      />
                      <SubMenuItem
                        label="Home Improvement"
                        img={{ src: '/img/pl-categories-home/HomeImprovement.png' }}
                        to={PL_HI_PATH}
                      />
                      <SubMenuItem
                        label="Major Purchase"
                        img={{ src: '/img/pl-categories-home/icon-major-purchases.png' }}
                        to={PL_MP_PATH}
                      />
                      <SubMenuItem label="Unexpected Expense" bgPosition="0px -120px" to={PL_UE_PATH} />
                    </MenuItem>
                    <MenuItem
                      label="Upgrade Cards"
                      id="uc"
                      onClick={handleMenuClick('uc')}
                      visibleSubMenuId={visibleSubMenuId}
                      icon={mdiCardBulletedOutline}
                      to={isNotFoundPage ? UPGRADE_CARD_PATH : ''}
                      isNotFoundPage={isNotFoundPage}
                    >
                      <SubMenuItem label="All Upgrade Cards" bgPosition="-2px -2px" to={UPGRADE_CARD_PATH} bold />
                      <SubMenuItem label="Cash Rewards" bgPosition="-60px -2px" to={PCL_CASH_CARD_PATH} />
                      <SubMenuItem label="Life Rewards" bgPosition="-2px -60px" to={PCL_LIFE_CARD_PATH} />
                      <SubMenuItem label="Upgrade Select" bgPosition="-60px -60px" to={PCL_SELECT_CARD_PATH} />
                      {isFlexPayPagesEnabled ? (
                        <SubMenuItem label="OneCard" img={{ src: '/img/one-card-no-bg.png' }} to={PCL_ONE_CARD_PATH} />
                      ) : null}
                    </MenuItem>
                    {!isFlexPayPagesEnabled ? (
                      <MenuItem icon={mdiCardBulletedOutline} label="OneCard" to="/one-card/" />
                    ) : null}
                    <MenuItem icon={mdiPiggyBankOutline} label="Savings" to={PREMIER_SAVINGS_PATH} />
                    {atmNetworkPageEnabled ? (
                      <MenuItem
                        label="Checking"
                        id="rc"
                        onClick={handleMenuClick('rc')}
                        visibleSubMenuId={visibleSubMenuId}
                        icon={mdiBankOutline}
                        to={isNotFoundPage ? REWARDS_CHECKING_PLUS_PATH : ''}
                        isNotFoundPage={isNotFoundPage}
                      >
                        <SubMenuItem
                          label="Rewards Checking Plus"
                          img={{ src: '/img/Grocery-Spot-Illustration.png' }}
                          to={REWARDS_CHECKING_PLUS_PATH}
                          bold
                        />
                        <SubMenuItem
                          label="ATM Network"
                          img={{ src: '/img/debit-card-sm.png' }}
                          to={ATM_NETWORK_PATH}
                          bold
                        />
                      </MenuItem>
                    ) : (
                      <MenuItem icon={mdiBankOutline} label="Checking" to={REWARDS_CHECKING_PLUS_PATH} />
                    )}
                    {isFlexPayPagesEnabled ? (
                      <MenuItem
                        label="Flex Pay"
                        id="bnpl"
                        onClick={handleMenuClick('bnpl')}
                        visibleSubMenuId={visibleSubMenuId}
                        icon={mdiCashClock}
                        to={isNotFoundPage ? FLEX_PAY : ''}
                        isNotFoundPage={isNotFoundPage}
                      >
                        <SubMenuItem
                          label="How It Works"
                          bold
                          img={{ src: '/img/suitcase-spot-illustration.png' }}
                          to={FLEX_PAY}
                        />
                        <SubMenuItem
                          label="Our Partners"
                          bold
                          img={{ src: '/img/flex-pay-flight-cruise.png' }}
                          to={FLEX_PAY_FIND_PARTNERS}
                        />
                        <SubMenuItem
                          label="Become a Partner"
                          bold
                          img={{ src: '/img/Vacation-Spot-Illustration.png' }}
                          to={FLEX_PAY_PARTNERS}
                        />
                      </MenuItem>
                    ) : null}
                    <MenuItem
                      icon={mdiViewGrid}
                      label="Credit Health"
                      id="ch"
                      onClick={handleMenuClick('ch')}
                      visibleSubMenuId={visibleSubMenuId}
                      to={isNotFoundPage ? CH_PATH : ''}
                      isNotFoundPage={isNotFoundPage}
                    >
                      <SubMenuItem
                        label="Credit Health Monitoring"
                        img={{ src: '/img/CreditHealth.png' }}
                        bold
                        to={CH_PATH}
                      />
                      <SubMenuItem
                        label="Credit Health & Insights"
                        img={{ src: '/img/blog.png' }}
                        bold
                        marginRight="md"
                        to={CREDIT_HEALTH_INSIGHTS_PATH}
                      />
                    </MenuItem>
                    <MenuItem icon={mdiHelpCircleOutline} label="Help" to="https://upgrade.zendesk.com/hc/en-us" />
                  </>
                )}
              </NavContainer>
            )}

            {showNavigation && (
              <>
                <StyledMobileSignInBtn
                  data-auto="mobile-sign-in"
                  className="hidden-md"
                  emphasis="secondary"
                  href={signInHref ?? '/portal/'}
                >
                  {signInText ?? 'Sign In'}
                </StyledMobileSignInBtn>
                <Padding all="md" className="hidden-md">
                  {customMobileNavigation}
                </Padding>
              </>
            )}
          </MenuWrapper>
        )}

        {!showPartnerLogo && showNavigation && (
          <NavigationContainer>
            {customNavigation}
            <Link className="header-nav-menu__btn" data-auto="desktop-sign-in" href={signInHref ?? '/portal/'}>
              {signInText ?? 'Sign In'}
            </Link>
          </NavigationContainer>
        )}

        {showPartnerLogo && <PartnerLogo {...partnerLogo} />}
      </Fluid>
    </HeaderContainer>
  );
};

export default HeaderMenu;
